import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useProjectContext } from '../../contexts/ProjectContext'
import { useUser } from '../../contexts/UserContext'
import { projectService } from '../../services/projectService'
import { userService } from '../../services/userService'
import { type Project, ProjectStatus } from '../../types/project'
import type { Contact, User, UserType } from '../../types/user'
import {
  isCurrentUserSender,
  shouldShowAcceptDeclineButtons,
} from '../../utils/fieldVisibility'
import {
  areAllInvitationsAccepted,
  canUserAcceptInvitations,
  getInvitationProgress,
  getInvitationStatusText,
} from '../../utils/invitationUtils'
import Modal from '../Modal'
import ProjectConfirmation from './ProjectConfirmation'

interface AcceptProjectModalProps {
  project: Project
  onClose: () => void
  onSuccess: () => void
}

type ReceiverAction = 'agree' | 'decline' | 'decline_with_comment' | null

// Maximum character limit for decline comment (half of B5 page ~ 1500 chars)
const DECLINE_COMMENT_MAX_LENGTH = 1500

const AcceptProjectModal: React.FC<AcceptProjectModalProps> = ({
  project: initialProject,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate()
  const { refreshProjects, acceptProjectInvite } = useProjectContext()
  const { user: currentUser } = useUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [creator, setCreator] = useState<User | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [userHasAccepted, setUserHasAccepted] = useState(false)
  // Use fresh project data to ensure invitations are loaded
  const [project, setProject] = useState<Project>(initialProject)

  // New state for receiver action selection
  const [selectedAction, setSelectedAction] = useState<ReceiverAction>(null)
  const [declineComment, setDeclineComment] = useState('')
  const [trucksAccepted, setTrucksAccepted] = useState<string>(
    project.number_of_trucks_accepted?.toString() ||
      project.trucks_number?.toString() ||
      '',
  )

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Use the centralized notification context to mark projects as read
  const { projects } = useNotificationContext()

  // Handle created_by which is now a User object
  const createdById = project.created_by?.id

  // Use the visibility utility to determine if current user is the sender
  const visibilityContext = {
    currentUser: currentUser ?? null,
    senderUserId: createdById,
    senderStakeholderType:
      project.created_by?.current_profile?.stakeholder_type,
  }
  const isCreator = isCurrentUserSender(visibilityContext)
  const showAcceptDecline = shouldShowAcceptDeclineButtons(visibilityContext)

  // Check if current user can accept invitations
  const canAccept = currentUser?.user_type
    ? canUserAcceptInvitations(currentUser.user_type as UserType)
    : false

  // Get invitation progress data
  const invitationProgress = getInvitationProgress(
    project.total_owner_invites_accepted,
    project.total_owner_invites_sent,
  )
  const allInvitationsAccepted = areAllInvitationsAccepted(
    project.total_owner_invites_accepted,
    project.total_owner_invites_sent,
  )

  // Check if current user has already accepted their invitation
  const checkUserAcceptanceStatus = useCallback(() => {
    if (project.invitations && currentUser?.email) {
      const userInvitation = project.invitations.find(
        (invite) => invite.email === currentUser.email,
      )
      return userInvitation?.accepted || false
    }
    return false
  }, [project.invitations, currentUser?.email])

  // Update user acceptance status when project data changes
  useEffect(() => {
    setUserHasAccepted(checkUserAcceptanceStatus())
  }, [checkUserAcceptanceStatus])

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch fresh project data to ensure invitations are loaded
        const freshProject = await projectService.getProject(initialProject.id)
        setProject(freshProject)

        const contactsData = await userService.getContacts()
        setContacts(contactsData)

        // We now have the full user object directly
        if (freshProject.created_by) {
          setCreator(freshProject.created_by)
        }
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      }
    }

    fetchData()
  }, [initialProject.id])

  const handleAccept = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      // Update trucks accepted if changed
      const trucksValue = trucksAccepted
        ? Number.parseInt(trucksAccepted, 10)
        : null
      if (
        trucksValue !== null &&
        trucksValue !== project.number_of_trucks_accepted
      ) {
        await projectService.updateProject(project.id, {
          number_of_trucks_accepted: trucksValue,
        })
      }

      // Check if this is a multi-party invitation system
      if (project.invitation_status) {
        // For multi-party invitations, we need to accept the invitation first
        if (!canAccept) {
          throw new Error(
            'Only project managers and administrators can accept project invitations.',
          )
        }

        // Find the invitation for the current user
        const userInvitation = project.invitations?.find(
          (invite) => invite.email === currentUser?.email && !invite.accepted,
        )

        if (!userInvitation) {
          throw new Error(
            'No pending invitation found for your email address. Please contact support.',
          )
        }

        // Call the actual invitation acceptance endpoint
        await acceptProjectInvite(project.id, userInvitation.id.toString())

        // OPTIMISTIC UI UPDATE
        setUserHasAccepted(true)

        if (project.invitations) {
          const updatedInvitations = project.invitations.map((invite) =>
            invite.email === currentUser?.email
              ? { ...invite, accepted: true }
              : invite,
          )
          project.invitations = updatedInvitations
        }

        project.total_owner_invites_accepted =
          (project.total_owner_invites_accepted || 0) + 1

        const updatedAllInvitationsAccepted = areAllInvitationsAccepted(
          project.total_owner_invites_accepted,
          project.total_owner_invites_sent,
        )

        try {
          await projects.markAsRead(project.id)
        } catch (error) {
          console.error('Failed to mark project as read:', error)
        }

        await refreshProjects()

        setTimeout(async () => {
          try {
            await refreshProjects()
          } catch (error) {
            console.warn('Failed to refresh projects for verification:', error)
          }
        }, 3000)

        if (updatedAllInvitationsAccepted) {
          navigate('/projects/accepted')
        }
      } else {
        // Legacy single-user acceptance
        await projectService.updateProjectStatus(
          project.id.toString(),
          ProjectStatus.ACCEPTED,
        )

        try {
          await projects.markAsRead(project.id)
        } catch (error) {
          console.error('Failed to mark project as read:', error)
        }

        await refreshProjects()
        navigate('/projects/accepted')
      }

      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to accept project:', error)
      let errorMessage = 'Failed to accept project'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDecline = async (comment: string) => {
    setIsSubmitting(true)
    try {
      await projectService.updateProjectStatus(
        project.id.toString(),
        ProjectStatus.DECLINED,
        comment || undefined,
      )

      try {
        await projects.markAsRead(project.id)
      } catch (error) {
        console.error('Failed to mark project as read:', error)
      }

      await refreshProjects()
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to decline project:', error)
      setError('Failed to decline project. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSendClick = () => {
    if (!selectedAction) return
    setShowConfirmation(true)
  }

  const handleConfirmSend = () => {
    setShowConfirmation(false)
    if (selectedAction === 'agree') {
      handleAccept()
    } else if (selectedAction === 'decline') {
      handleDecline('')
    } else if (selectedAction === 'decline_with_comment') {
      handleDecline(declineComment)
    }
  }

  const handleCancelConfirmation = () => {
    setShowConfirmation(false)
  }

  // Check if Send button should be enabled
  // Trucks accepted is required when agreeing
  const trucksValid =
    selectedAction !== 'agree' ||
    (trucksAccepted && Number.parseInt(trucksAccepted, 10) >= 1)
  const isSendEnabled =
    selectedAction !== null &&
    trucksValid &&
    (selectedAction !== 'decline_with_comment' ||
      declineComment.trim().length > 0)

  // Get confirmation message based on selected action
  const getConfirmationMessage = () => {
    if (selectedAction === 'agree') {
      return 'By selecting and then clicking on the [I Agree] button I indicate I actually have read the provisions of the Project Terms and I expressly agree to the provisions of the Project Terms.'
    } else if (selectedAction === 'decline') {
      return 'By selecting and then clicking on the [Decline] button I indicate I expressly decline the provisions of the Project Terms.'
    } else if (selectedAction === 'decline_with_comment') {
      return 'By selecting and then clicking on the [Decline with Comment] button I indicate I expressly decline the provisions of the Project Terms with the following comment.'
    }
    return ''
  }

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <div className={css(styles.headerSubRow)}>
            {project.label && (
              <div className={css(styles.projectLabel)}>
                ID: {project.label}
              </div>
            )}
            {project.sent_at_formatted && (
              <div className={css(styles.sentDate)}>
                Sent: {project.sent_at_formatted}
              </div>
            )}
          </div>
          <h2 className={css(styles.title)}>Project Terms – Pending</h2>
        </div>
        <div className={css(styles.content)}>
          <ProjectConfirmation
            data={project}
            contacts={contacts}
            creator={creator}
            useDirectData={true}
            visibilityContext={visibilityContext}
            templateRecipientType={
              project.template_recipient_type as
                | 'BROKER'
                | 'SUB_HAULER'
                | 'THIRD_PARTY_CONTRACTOR'
                | null
            }
          />

          {/* Multi-party Invitation Progress */}
          {project.invitation_status && (
            <div className={css(styles.invitationSection)}>
              <h3 className={css(styles.sectionTitle)}>Invitation Status</h3>
              <div className={css(styles.invitationStatus)}>
                <div className={css(styles.statusInfo)}>
                  <span className={css(styles.statusLabel)}>
                    {getInvitationStatusText(project.invitation_status)}
                  </span>
                  <span className={css(styles.progressText)}>
                    {project.total_owner_invites_accepted || 0} of{' '}
                    {project.total_owner_invites_sent || 0} accepted
                  </span>
                </div>
                <div className={css(styles.progressBar)}>
                  <div
                    className={css(styles.progressFill)}
                    style={{ width: `${invitationProgress}%` }}
                  />
                </div>
              </div>
              {!canAccept && (
                <div className={css(styles.warningMessage)}>
                  Only project managers and administrators can accept project
                  invitations.
                </div>
              )}
              {canAccept && !allInvitationsAccepted && (
                <div className={css(styles.infoMessage)}>
                  {userHasAccepted
                    ? 'You have accepted your invitation. Waiting for other parties to accept.'
                    : 'You can accept your invitation now. The project will be fully accepted once all parties accept.'}
                </div>
              )}
            </div>
          )}

          {/* Receiver Editable Fields Section */}
          {showAcceptDecline && !userHasAccepted && canAccept && (
            <div className={css(styles.receiverSection)}>
              <h3 className={css(styles.sectionTitle)}>Your Response</h3>

              {/* Number of Trucks (Accepted) Field - Required */}
              <div className={css(styles.fieldGroup)}>
                <label className={css(styles.fieldLabel)}>
                  Number of Trucks (Accepted){' '}
                  <span className={css(styles.required)}>*</span>
                </label>
                <p className={css(styles.fieldHelp)}>
                  Confirm how many trucks you will provide
                </p>
                <input
                  type="number"
                  min="1"
                  value={trucksAccepted}
                  onChange={(e) => setTrucksAccepted(e.target.value)}
                  className={css(
                    styles.input,
                    !trucksAccepted && styles.inputError,
                  )}
                  placeholder={
                    project.trucks_number
                      ? `Requested: ${project.trucks_number}`
                      : 'Enter number of trucks'
                  }
                />
                {!trucksAccepted && (
                  <span className={css(styles.fieldError)}>
                    Number of trucks is required
                  </span>
                )}
              </div>

              {/* Action Selection */}
              <div className={css(styles.actionSelection)}>
                <label className={css(styles.fieldLabel)}>
                  Select your response
                </label>

                {/* I Agree Option */}
                <label className={css(styles.radioOption)}>
                  <input
                    type="radio"
                    name="receiver_action"
                    value="agree"
                    checked={selectedAction === 'agree'}
                    onChange={() => setSelectedAction('agree')}
                    className={css(styles.radioInput)}
                  />
                  <span
                    className={css(
                      styles.radioLabel,
                      selectedAction === 'agree' && styles.radioLabelSelected,
                    )}
                  >
                    <i className="fa-solid fa-check-circle" />I Agree
                  </span>
                  <span className={css(styles.radioDescription)}>
                    Accept the Project Terms
                  </span>
                </label>

                {/* Decline Option */}
                <label className={css(styles.radioOption)}>
                  <input
                    type="radio"
                    name="receiver_action"
                    value="decline"
                    checked={selectedAction === 'decline'}
                    onChange={() => setSelectedAction('decline')}
                    className={css(styles.radioInput)}
                  />
                  <span
                    className={css(
                      styles.radioLabel,
                      selectedAction === 'decline' && styles.radioLabelDecline,
                    )}
                  >
                    <i className="fa-solid fa-times-circle" />
                    Decline
                  </span>
                  <span className={css(styles.radioDescription)}>
                    Decline without comment
                  </span>
                </label>

                {/* Decline with Comment Option */}
                <label className={css(styles.radioOption)}>
                  <input
                    type="radio"
                    name="receiver_action"
                    value="decline_with_comment"
                    checked={selectedAction === 'decline_with_comment'}
                    onChange={() => setSelectedAction('decline_with_comment')}
                    className={css(styles.radioInput)}
                  />
                  <span
                    className={css(
                      styles.radioLabel,
                      selectedAction === 'decline_with_comment' &&
                        styles.radioLabelDecline,
                    )}
                  >
                    <i className="fa-solid fa-comment" />
                    Decline with Comment
                  </span>
                  <span className={css(styles.radioDescription)}>
                    Decline with a comment (max {DECLINE_COMMENT_MAX_LENGTH}{' '}
                    characters)
                  </span>
                </label>

                {/* Decline Comment Text Area */}
                {selectedAction === 'decline_with_comment' && (
                  <div className={css(styles.commentSection)}>
                    <textarea
                      value={declineComment}
                      onChange={(e) =>
                        setDeclineComment(
                          e.target.value.slice(0, DECLINE_COMMENT_MAX_LENGTH),
                        )
                      }
                      className={css(styles.textarea)}
                      placeholder="Enter your reason for declining..."
                      rows={5}
                      maxLength={DECLINE_COMMENT_MAX_LENGTH}
                    />
                    <div className={css(styles.charCount)}>
                      {declineComment.length} / {DECLINE_COMMENT_MAX_LENGTH}{' '}
                      characters
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && <div className={css(styles.errorMessage)}>{error}</div>}
        </div>

        <div className={css(styles.footer)}>
          <div className={css(styles.footerLeft)}>
            <button onClick={onClose} className={css(styles.closeBtn)}>
              Close
            </button>
          </div>
          <div className={css(styles.footerRight)}>
            {showAcceptDecline &&
              (allInvitationsAccepted ? (
                <button className={css(styles.acceptedBtn)} disabled={true}>
                  Fully Accepted
                </button>
              ) : userHasAccepted ? (
                <button className={css(styles.waitingBtn)} disabled={true}>
                  Waiting for others to accept...
                </button>
              ) : canAccept ? (
                <button
                  onClick={handleSendClick}
                  className={css(
                    styles.sendBtn,
                    !isSendEnabled && styles.sendBtnDisabled,
                  )}
                  disabled={!isSendEnabled || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      &nbsp;Sending...
                    </>
                  ) : (
                    'Send'
                  )}
                </button>
              ) : (
                <div className={css(styles.viewOnlyBadge)}>View Only</div>
              ))}
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className={css(styles.confirmationOverlay)}>
            <div className={css(styles.confirmationModal)}>
              <div className={css(styles.confirmationHeader)}>
                <h3 className={css(styles.confirmationTitle)}>
                  {selectedAction === 'agree'
                    ? 'Confirm Agreement'
                    : 'Confirm Decline'}
                </h3>
              </div>
              <div className={css(styles.confirmationContent)}>
                <p className={css(styles.confirmationText)}>
                  {getConfirmationMessage()}
                </p>
                {selectedAction === 'decline_with_comment' &&
                  declineComment && (
                    <div className={css(styles.commentPreview)}>
                      <strong>Your comment:</strong>
                      <p>{declineComment}</p>
                    </div>
                  )}
              </div>
              <div className={css(styles.confirmationFooter)}>
                <button
                  onClick={handleConfirmSend}
                  className={css(
                    selectedAction === 'agree'
                      ? styles.confirmationAcceptBtn
                      : styles.confirmationDeclineBtn,
                  )}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Processing...'
                    : selectedAction === 'agree'
                      ? 'Yes, I agree to the Project Terms'
                      : 'Yes, I decline the Project Terms'}
                </button>
                <button
                  onClick={handleCancelConfirmation}
                  className={css(styles.confirmationCancelBtn)}
                >
                  No, I made an error
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    textAlign: 'center',
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  poNumber: {
    fontSize: '14px',
    color: '#6B7280',
  },
  headerSubRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  projectLabel: {
    fontSize: '14px',
    color: '#6B7280',
  },
  sentDate: {
    fontSize: '14px',
    color: '#6B7280',
  },
  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    display: 'flex',
    gap: '8px',
  },
  footerRight: {
    display: 'flex',
    gap: '8px',
  },
  closeBtn: {
    backgroundColor: '#FFFFFF',
    color: '#000000',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    padding: '8px 20px',
    height: '40px',
    minWidth: '100px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    ':hover': {
      backgroundColor: '#F9FAFB',
      borderColor: '#9CA3AF',
    },
  },
  sendBtn: {
    backgroundColor: '#10B981',
    color: '#000000',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 20px',
    height: '40px',
    minWidth: '100px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  sendBtnDisabled: {
    backgroundColor: '#E5E7EB',
    color: '#6B7280',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  invitationSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  sectionTitle: {
    margin: 0,
    marginBottom: '16px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  invitationStatus: {
    marginBottom: '16px',
  },
  statusInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  statusLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
  },
  progressText: {
    fontSize: '12px',
    color: '#6B7280',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#E5E7EB',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#10B981',
    transition: 'width 0.3s ease',
  },
  warningMessage: {
    fontSize: '12px',
    color: '#F59E0B',
    backgroundColor: '#FEF3C7',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
  },
  infoMessage: {
    fontSize: '12px',
    color: '#2563EB',
    backgroundColor: '#EFF6FF',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
  },
  errorMessage: {
    fontSize: '12px',
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '16px',
  },
  viewOnlyBadge: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
  },
  waitingBtn: {
    backgroundColor: '#F59E0B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'not-allowed',
    opacity: 0.8,
  },
  acceptedBtn: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'default',
  },
  receiverSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  fieldGroup: {
    marginBottom: '20px',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '4px',
  },
  fieldHelp: {
    fontSize: '12px',
    color: '#6B7280',
    margin: '0 0 8px 0',
  },
  input: {
    width: '100%',
    maxWidth: '200px',
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.1)',
    },
  },
  inputError: {
    borderColor: '#EF4444',
    ':focus': {
      borderColor: '#EF4444',
      boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.1)',
    },
  },
  required: {
    color: '#EF4444',
  },
  fieldError: {
    display: 'block',
    fontSize: '12px',
    color: '#EF4444',
    marginTop: '4px',
  },
  actionSelection: {
    marginTop: '20px',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: '#D1D5DB',
      backgroundColor: '#F9FAFB',
    },
  },
  radioInput: {
    marginRight: '12px',
    marginTop: '2px',
    width: '18px',
    height: '18px',
    accentColor: '#2563EB',
  },
  radioLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '180px',
  },
  radioLabelSelected: {
    color: '#059669',
  },
  radioLabelDecline: {
    color: '#DC2626',
  },
  radioDescription: {
    fontSize: '13px',
    color: '#6B7280',
    flex: 1,
  },
  commentSection: {
    marginTop: '8px',
    marginLeft: '30px',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.1)',
    },
  },
  charCount: {
    textAlign: 'right',
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '4px',
  },
  confirmationOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  confirmationModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '550px',
    margin: '20px',
  },
  confirmationHeader: {
    padding: '24px 24px 0 24px',
  },
  confirmationTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  confirmationContent: {
    padding: '16px 24px',
  },
  confirmationText: {
    margin: 0,
    fontSize: '14px',
    color: '#374151',
    lineHeight: 1.6,
  },
  commentPreview: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    fontSize: '13px',
    color: '#374151',
  },
  confirmationFooter: {
    padding: '16px 24px 24px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  confirmationCancelBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  confirmationAcceptBtn: {
    backgroundColor: '#10B981',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmationDeclineBtn: {
    backgroundColor: '#DC2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#B91C1C',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
})

export default AcceptProjectModal
